import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { Injectable, Injector, OnDestroy, afterNextRender, inject } from '@angular/core';

/**
 * Focus d'une popup : pris à l'ouverture, gardé dedans tant qu'elle est ouverte — Tab ne repart
 * pas dans la page derrière elle — et rendu à la fermeture à l'élément qui l'avait.
 *
 * Les éléments sont passés comme fonctions : ils n'existent qu'une fois la popup affichée, et
 * sont lus après le rendu.
 */
@Injectable()
export class DialogFocus implements OnDestroy {
  private readonly focusTrapFactory = inject(FocusTrapFactory);
  private readonly injector = inject(Injector);

  private trap: FocusTrap | null = null;
  /** Élément qui avait le focus avant l'ouverture. */
  private previous: HTMLElement | null = null;

  /** Une fois la popup affichée, garde le focus dans `container` et le pose sur `initial`. */
  open(container: () => HTMLElement | undefined, initial: () => HTMLElement | undefined): void {
    if (!this.previous && document.activeElement instanceof HTMLElement) {
      this.previous = document.activeElement;
    }
    this.afterRender(() => {
      const element = container();
      if (!element) {
        return;
      }
      this.trap?.destroy();
      this.trap = this.focusTrapFactory.create(element);
      initial()?.focus();
    });
  }

  /** Déplace le focus une fois la vue à jour, quand l'élément qui l'avait disparaît. */
  moveTo(target: () => HTMLElement | undefined): void {
    this.afterRender(() => target()?.focus());
  }

  /** Suspend la garde sans rendre le focus : la popup se cache le temps d'une resélection. */
  suspend(): void {
    this.trap?.destroy();
    this.trap = null;
  }

  /** Libère le focus et le rend à l'élément qui l'avait avant l'ouverture, s'il est encore là. */
  close(): void {
    this.suspend();
    if (this.previous?.isConnected) {
      this.previous.focus();
    }
    this.previous = null;
  }

  ngOnDestroy(): void {
    this.suspend();
  }

  private afterRender(callback: () => void): void {
    afterNextRender(callback, { injector: this.injector });
  }
}
